/**
 * Date and Market Calendar Utilities for pvapp
 * Supports both TSX (Canada) and US (NYSE/NASDAQ) trading schedules and holidays.
 */

/**
 * Returns today's date formatted as YYYY-MM-DD in the specified timezone (or user's preferred timezone).
 * Uses 'en-CA' locale with Intl.DateTimeFormat which standardizes to YYYY-MM-DD format.
 */
export const getTodayInTimezone = (timeZone = 'America/New_York') => {
    try {
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timeZone || 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        return formatter.format(new Date());
    } catch (e) {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
};

/**
 * Formats a Date object or ISO date string into YYYY-MM-DD in the specified timezone.
 */
export const formatDateInTimezone = (date, timeZone = 'America/New_York') => {
    if (!date) return '';
    try {
        const d = typeof date === 'string' ? new Date(date) : date;
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timeZone || 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        return formatter.format(d);
    } catch (e) {
        return typeof date === 'string' ? date.split('T')[0] : date.toISOString().split('T')[0];
    }
};

/**
 * Computes Easter Sunday using the Meeus/Jones/Butcher algorithm.
 * Returns a Date object in UTC.
 */
export const getEasterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Returns Good Friday date for the given year as 'YYYY-MM-DD'.
 */
export const getGoodFriday = (year) => {
    const easter = getEasterSunday(year);
    const goodFriday = new Date(easter.getTime() - (2 * 24 * 60 * 60 * 1000));
    return goodFriday.toISOString().split('T')[0];
};

/**
 * Finds the Nth occurrence of a weekday in a given month.
 * month: 1-12
 * dayOfWeek: 0 = Sun, 1 = Mon, ..., 6 = Sat
 * n: 1 = 1st, 2 = 2nd, 3 = 3rd, 4 = 4th, -1 = last
 */
export const getNthWeekdayOfMonth = (year, month, dayOfWeek, n) => {
    if (n > 0) {
        let count = 0;
        for (let day = 1; day <= 31; day++) {
            const d = new Date(Date.UTC(year, month - 1, day));
            if (d.getUTCMonth() !== month - 1) break;
            if (d.getUTCDay() === dayOfWeek) {
                count++;
                if (count === n) {
                    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                }
            }
        }
    } else if (n === -1) {
        // Last occurrence in month
        for (let day = 31; day >= 1; day--) {
            const d = new Date(Date.UTC(year, month - 1, day));
            if (d.getUTCMonth() === month - 1 && d.getUTCDay() === dayOfWeek) {
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }
        }
    }
    return '';
};

/**
 * Returns the Monday immediately preceding May 25 (Victoria Day in Canada).
 */
export const getVictoriaDay = (year) => {
    for (let day = 24; day >= 18; day--) {
        const d = new Date(Date.UTC(year, 4, day)); // Month 4 is May
        if (d.getUTCDay() === 1) {
            return `${year}-05-${String(day).padStart(2, '0')}`;
        }
    }
    return `${year}-05-24`;
};

/**
 * Returns a Set of US stock market holiday dates (NYSE/NASDAQ) formatted as 'YYYY-MM-DD'.
 */
export const getUsMarketHolidays = (year) => {
    const holidays = new Set();

    // 1. New Year's Day (Jan 1)
    const ny = new Date(Date.UTC(year, 0, 1));
    if (ny.getUTCDay() === 6) holidays.add(`${year - 1}-12-31`);
    else if (ny.getUTCDay() === 0) holidays.add(`${year}-01-02`);
    else holidays.add(`${year}-01-01`);

    // 2. Martin Luther King Jr. Day (3rd Monday in January)
    holidays.add(getNthWeekdayOfMonth(year, 1, 1, 3));

    // 3. Washington's Birthday / Presidents' Day (3rd Monday in February)
    holidays.add(getNthWeekdayOfMonth(year, 2, 1, 3));

    // 4. Good Friday
    holidays.add(getGoodFriday(year));

    // 5. Memorial Day (Last Monday in May)
    holidays.add(getNthWeekdayOfMonth(year, 5, 1, -1));

    // 6. Juneteenth (June 19)
    const jt = new Date(Date.UTC(year, 5, 19));
    if (jt.getUTCDay() === 6) holidays.add(`${year}-06-18`);
    else if (jt.getUTCDay() === 0) holidays.add(`${year}-06-20`);
    else holidays.add(`${year}-06-19`);

    // 7. Independence Day (July 4)
    const ind = new Date(Date.UTC(year, 6, 4));
    if (ind.getUTCDay() === 6) holidays.add(`${year}-07-03`);
    else if (ind.getUTCDay() === 0) holidays.add(`${year}-07-05`);
    else holidays.add(`${year}-07-04`);

    // 8. Labor Day (1st Monday in September)
    holidays.add(getNthWeekdayOfMonth(year, 9, 1, 1));

    // 9. Thanksgiving Day (4th Thursday in November)
    holidays.add(getNthWeekdayOfMonth(year, 11, 4, 4));

    // 10. Christmas Day (Dec 25)
    const xm = new Date(Date.UTC(year, 11, 25));
    if (xm.getUTCDay() === 6) holidays.add(`${year}-12-24`);
    else if (xm.getUTCDay() === 0) holidays.add(`${year}-12-26`);
    else holidays.add(`${year}-12-25`);

    return holidays;
};

/**
 * Returns a Set of TSX stock market holiday dates (Canada) formatted as 'YYYY-MM-DD'.
 */
export const getTsxMarketHolidays = (year) => {
    const holidays = new Set();

    // 1. New Year's Day (Jan 1)
    const ny = new Date(Date.UTC(year, 0, 1));
    if (ny.getUTCDay() === 0) holidays.add(`${year}-01-02`);
    else if (ny.getUTCDay() === 6) holidays.add(`${year - 1}-12-31`);
    else holidays.add(`${year}-01-01`);

    // 2. Family Day (3rd Monday in February)
    holidays.add(getNthWeekdayOfMonth(year, 2, 1, 3));

    // 3. Good Friday
    holidays.add(getGoodFriday(year));

    // 4. Victoria Day (Monday before May 25)
    holidays.add(getVictoriaDay(year));

    // 5. Canada Day (July 1, observed July 2 if Sunday)
    const cd = new Date(Date.UTC(year, 6, 1));
    if (cd.getUTCDay() === 0) holidays.add(`${year}-07-02`);
    else holidays.add(`${year}-07-01`);

    // 6. Civic Holiday (1st Monday in August)
    holidays.add(getNthWeekdayOfMonth(year, 8, 1, 1));

    // 7. Labour Day (1st Monday in September)
    holidays.add(getNthWeekdayOfMonth(year, 9, 1, 1));

    // 8. Thanksgiving Day (Canada, 2nd Monday in October)
    holidays.add(getNthWeekdayOfMonth(year, 10, 1, 2));

    // 9. Christmas Day (Dec 25)
    const xm = new Date(Date.UTC(year, 11, 25));
    if (xm.getUTCDay() === 6) holidays.add(`${year}-12-27`);
    else if (xm.getUTCDay() === 0) holidays.add(`${year}-12-26`);
    else holidays.add(`${year}-12-25`);

    // 10. Boxing Day (Dec 26)
    const bx = new Date(Date.UTC(year, 11, 26));
    if (bx.getUTCDay() === 6) holidays.add(`${year}-12-28`);
    else if (bx.getUTCDay() === 0) holidays.add(`${year}-12-28`);
    else if (bx.getUTCDay() === 1) holidays.add(`${year}-12-27`);
    else holidays.add(`${year}-12-26`);

    return holidays;
};

/**
 * Determines whether a given date (YYYY-MM-DD) is a market holiday for the requested exchange.
 * exchange: 'US' | 'TSX' | 'BOTH'
 */
export const isMarketHoliday = (dateStr, exchange = 'BOTH') => {
    if (!dateStr) return false;
    const year = parseInt(dateStr.split('-')[0], 10);
    if (isNaN(year)) return false;

    const usHolidays = getUsMarketHolidays(year);
    const tsxHolidays = getTsxMarketHolidays(year);

    if (exchange === 'US') {
        return usHolidays.has(dateStr);
    }
    if (exchange === 'TSX') {
        return tsxHolidays.has(dateStr);
    }
    // For 'BOTH', it's only a market-wide closure if BOTH exchanges are closed
    return usHolidays.has(dateStr) && tsxHolidays.has(dateStr);
};

/**
 * Checks if a given date (YYYY-MM-DD) is an active trading day for the requested exchange.
 * A trading day must be Monday-Friday and NOT a market holiday.
 * For 'BOTH', it is a trading day if EITHER US or TSX is open.
 */
export const isMarketTradingDay = (dateStr, exchange = 'BOTH') => {
    if (!dateStr) return false;
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dayOfWeek = dt.getUTCDay();

    // Weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
    }

    const usHolidays = getUsMarketHolidays(y);
    const tsxHolidays = getTsxMarketHolidays(y);

    if (exchange === 'US') {
        return !usHolidays.has(dateStr);
    }
    if (exchange === 'TSX') {
        return !tsxHolidays.has(dateStr);
    }
    // For 'BOTH', open if either is open
    return !usHolidays.has(dateStr) || !tsxHolidays.has(dateStr);
};

/**
 * Determines portfolio market exposure from holdings:
 * Returns 'CA_ONLY', 'US_ONLY', or 'BOTH'.
 */
export const getPortfolioMarketExposure = (holdings = []) => {
    if (!holdings || holdings.length === 0) return 'BOTH';

    let hasCa = false;
    let hasUs = false;

    for (const h of holdings) {
        const sym = (typeof h === 'string' ? h : (h.symbol || '')).toUpperCase();
        const ccy = (typeof h === 'object' && h.currency ? (h.currency.value || h.currency) : '').toUpperCase();

        const isCanadian = sym.endsWith('.TO') || sym.endsWith('.V') || sym.endsWith('.NE') || sym.endsWith('.CSE') || ccy === 'CAD';
        if (isCanadian) {
            hasCa = true;
        } else {
            hasUs = true;
        }
    }

    if (hasCa && !hasUs) return 'CA_ONLY';
    if (hasUs && !hasCa) return 'US_ONLY';
    return 'BOTH';
};

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Resolves the dynamic Day's Change label (e.g., 'Today', 'Friday', 'Thursday', 'Monday')
 * based on live quote timestamp or market session schedule.
 */
export const getMarketSessionDayLabel = ({
    holdings = [],
    quoteTimestamp = null,
    timeZone = 'America/New_York',
    referenceDate = null
} = {}) => {
    const now = referenceDate ? (typeof referenceDate === 'string' ? new Date(referenceDate) : referenceDate) : new Date();

    // Format current date and time in Eastern Time (ET) which governs NYSE/NASDAQ and TSX
    const etFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });

    const parts = etFormatter.formatToParts(now);
    const partMap = {};
    parts.forEach(p => { partMap[p.type] = p.value; });

    const currentEtDateStr = `${partMap.year}-${partMap.month}-${partMap.day}`;
    const currentEtHour = parseInt(partMap.hour, 10);
    const currentEtMinute = parseInt(partMap.minute, 10);
    const currentEtTimeInMinutes = currentEtHour * 60 + currentEtMinute;
    const marketOpenMinutes = 9 * 60 + 30; // 9:30 AM ET

    // Determine exchange exposure
    const exposure = getPortfolioMarketExposure(holdings);
    const exchangeType = exposure === 'CA_ONLY' ? 'TSX' : (exposure === 'US_ONLY' ? 'US' : 'BOTH');

    // 1. If a quote timestamp is provided, use its actual market session date
    if (quoteTimestamp) {
        const qDate = typeof quoteTimestamp === 'string' ? new Date(quoteTimestamp) : quoteTimestamp;
        const qDateStr = formatDateInTimezone(qDate, 'America/New_York');

        if (qDateStr === currentEtDateStr && currentEtTimeInMinutes >= marketOpenMinutes && isMarketTradingDay(currentEtDateStr, exchangeType)) {
            return 'Today';
        }

        const [qy, qm, qd] = qDateStr.split('-').map(Number);
        const quoteDt = new Date(Date.UTC(qy, qm - 1, qd));
        return WEEKDAY_NAMES[quoteDt.getUTCDay()] || 'Today';
    }

    // 2. Schedule-based resolution
    const isTodayTradingDay = isMarketTradingDay(currentEtDateStr, exchangeType);

    // If today is a trading day and market has opened (>= 9:30 AM ET), it is "Today"
    if (isTodayTradingDay && currentEtTimeInMinutes >= marketOpenMinutes) {
        return 'Today';
    }

    // Otherwise, step backward to find the previous active trading day
    const [cy, cm, cd] = currentEtDateStr.split('-').map(Number);
    let checkDate = new Date(Date.UTC(cy, cm - 1, cd));

    // Look back up to 10 days to find the last trading day
    for (let i = 1; i <= 10; i++) {
        checkDate = new Date(checkDate.getTime() - (24 * 60 * 60 * 1000));
        const checkStr = checkDate.toISOString().split('T')[0];
        if (isMarketTradingDay(checkStr, exchangeType)) {
            return WEEKDAY_NAMES[checkDate.getUTCDay()];
        }
    }

    return 'Friday';
};
